import { useCallback, useEffect, useMemo, useState } from 'react';
import { TrackGroupTypes, trackTableClicked } from '../../analytics/utils';
import { PreferenceClient } from '../../client/preference.client';
import { BasicTrackingFn, EventElementType, Params, ValueSet } from '../../types';
import { LeaderboardInfo } from './data';
import { createProductGroupsParam, findLeaderboardItemNamesForTracking, leaderboardGroupsIntoParams } from './helper';
import { GroupType, ProductGroup } from './types';

export interface Props {
  componentSettings: Params;
  preferencesGroupName: string;
  groupsParam: string;
  updateSettings: (settings: Params) => void;
  paramsForData: Params;
  trackEvent: BasicTrackingFn;
  leaderboardData?: LeaderboardInfo;
}

export const useProductGroups = ({
  componentSettings,
  updateSettings,
  preferencesGroupName,
  paramsForData,
  groupsParam,
  trackEvent,
  leaderboardData,
}: Props) => {
  const [preferences, setPreferences] = useState<ValueSet>({});
  const [groups, setGroups] = useState<ProductGroup[]>();
  const [isGroupModalOpen, setIsGroupModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const itemGroups = useMemo(() => [...new Set(paramsForData.cell?.map(cell => cell?.split('-')[1]))], [paramsForData]);

  const createGroups = ({ leaderboardData }: { leaderboardData?: LeaderboardInfo } = {}): ProductGroup[] => {
    return Object.keys(preferences).map(group => {
      const groupType = !leaderboardData
        ? null
        : leaderboardData.rowIds.some(rowId => rowId === group)
          ? GroupType.selectGroups
          : GroupType.otherGroups;

      return {
        id: group,
        name: group,
        modelIds: preferences[group].modelIds,
        selected: false,
        groupType,
      };
    });
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    getProductGroups();
  }, [preferencesGroupName]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!preferences) return;
    const mapped = createGroups();

    setGroups(mapped);
  }, [preferences]);

  const handleProductGroups = useCallback(
    (value: any) => {
      const paramGroup = leaderboardGroupsIntoParams(value);
      updateSettings({
        ...componentSettings,
        [groupsParam]: paramGroup ? paramGroup : [],
      });
    },
    [componentSettings, updateSettings]
  );

  const preferencesStore = (updatedPreferences: ValueSet) => {
    setIsLoading(true);
    setPreferences(updatedPreferences);
    return PreferenceClient.getInstance()
      .store(preferencesGroupName, updatedPreferences)
      .then(response => {
        const productGroups = createProductGroupsParam(response);
        handleProductGroups(productGroups);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const createOrUpdateGroup = (names: string[], selected: string[], rowIdField: string, rowNamefield: string) => {
    if (!leaderboardData) return;
    // add selected products to preferences keep the existing ones
    const updatedPreferences: ValueSet = { ...preferences };
    names.forEach(name => {
      const modelIds = [...new Set([...selected, ...(preferences?.[name]?.modelIds || [])])];

      updatedPreferences[name] = { modelIds, itemGroups };
    });

    const groupType = names.some(name => preferences[name]) ? TrackGroupTypes.AddItem : TrackGroupTypes.AddGroup;
    const selectedNames = findLeaderboardItemNamesForTracking(selected, rowIdField, rowNamefield, leaderboardData);
    trackTableClicked(trackEvent, groupType, EventElementType.Group, {
      optionChosen: selectedNames,
      numberOfProducts: `${selected.length}`,
    });

    setPreferences(updatedPreferences);
    preferencesStore(updatedPreferences);
  };

  const toggleCreateGroupModal = () => {
    setIsGroupModalOpen(prev => !prev);
  };

  const getProductGroups = async () => {
    setIsLoading(true);
    try {
      const response = await PreferenceClient.getInstance().retrieve(preferencesGroupName);
      const productGroups = createProductGroupsParam(response);
      setPreferences(response || {}); // Update preferences state
      handleProductGroups(productGroups); // Handle product groups
    } finally {
      setIsLoading(false);
    }
  };

  const deleteGroup = (name: string) => {
    trackTableClicked(trackEvent, TrackGroupTypes.DeleteGroup, EventElementType.Group);
    const updatedPreferences = structuredClone(preferences);
    delete updatedPreferences[name];
    setPreferences(updatedPreferences);
    preferencesStore(updatedPreferences);
  };

  const deleteGroupItem = (name: string, itemId: string) => {
    trackTableClicked(trackEvent, TrackGroupTypes.RemoveItem, EventElementType.Group);
    if (preferences[name].modelIds.length === 1) {
      deleteGroup(name);
      return;
    }
    const updatedPreferences = structuredClone(preferences);
    updatedPreferences[name].modelIds = updatedPreferences[name].modelIds.filter(id => id !== itemId);
    setPreferences(updatedPreferences);
    preferencesStore(updatedPreferences);
  };

  const updateGroupName = (oldName: string, newName: string) => {
    const updatedPreferences = { ...preferences };
    updatedPreferences[newName] = updatedPreferences[oldName];
    delete updatedPreferences[oldName];
    trackTableClicked(trackEvent, TrackGroupTypes.RenameGroup, EventElementType.Group);
    setPreferences(updatedPreferences);
    preferencesStore(updatedPreferences);
  };

  return {
    groups,
    isLoading,
    isGroupModalOpen,
    toggleCreateGroupModal,
    createOrUpdateGroup,
    deleteGroup,
    deleteGroupItem,
    updateGroupName,
    getProductGroups, //exporting for test
    preferencesStore, //exporting for test
    createGroups,
  };
};
